import React, { useEffect, useMemo, useRef, useState } from 'react'
import DOMPurify from 'dompurify'
import { PrintArea } from './PrintArea'
import { renderTextEditorSourceCode } from './Box'
import { getReportFlowGroups, paginateReportFlow } from './reportFlow.util'
import './FlowPrintLayout.scss'

const escapeHtml = (value) => String(value || '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]))

const fragmentHtml = (blocks, title) =>
  `${title ? `<div class="report-flow-title">${escapeHtml(title)}</div>` : ''}${blocks.join('')}`

// Preserve inline markup and Thai combining characters when splitting a long
// paragraph. No source HTML is evaluated as JavaScript.
const sliceTextHtml = (html, start, end) => {
  const root = document.createElement('div')
  root.innerHTML = html
  const wrapper = root.firstElementChild || root
  const walker = document.createTreeWalker(wrapper, NodeFilter.SHOW_TEXT)
  const nodes = []
  let offset = 0
  while (walker.nextNode()) {
    const node = walker.currentNode
    nodes.push({ node, start: offset, end: offset + node.length })
    offset += node.length
  }
  const first = nodes.find((entry) => entry.end > start)
  const last = nodes.find((entry) => entry.end >= end)
  if (!first || !last) return ''
  const range = document.createRange()
  range.setStart(first.node, start - first.start)
  range.setEnd(last.node, end - last.start)
  // cloneContents omits ancestors shared by both boundaries (e.g. <strong>
  // around one text node). Restore those wrappers so continuation text keeps
  // the same font metrics and formatting as the source being measured.
  let contents = range.cloneContents()
  let ancestor = range.commonAncestorContainer
  if (ancestor.nodeType === Node.TEXT_NODE) ancestor = ancestor.parentNode
  while (ancestor && ancestor !== wrapper) {
    const parent = ancestor.cloneNode(false)
    parent.appendChild(contents)
    contents = parent
    ancestor = ancestor.parentNode
  }
  const clone = wrapper.cloneNode(false)
  clone.appendChild(contents)
  return clone.outerHTML
}

export const splitMeasuredFlowBlock = (html, available, title, measure) => {
  const root = document.createElement('div')
  root.innerHTML = html
  if (root.querySelector('img,table,svg,canvas,iframe')) return null
  const text = root.textContent || ''
  if (!text || !Intl.Segmenter) return null
  const boundaries = [...new Intl.Segmenter('th', { granularity: 'grapheme' }).segment(text)]
    .map((part) => part.index + part.segment.length)
  let low = 0
  let high = boundaries.length - 2
  let found = -1
  while (low <= high) {
    const middle = Math.floor((low + high) / 2)
    const first = sliceTextHtml(html, 0, boundaries[middle])
    if (measure([first], title) <= available) {
      found = middle
      low = middle + 1
    } else high = middle - 1
  }
  if (found < 0) return null
  const cut = boundaries[found]
  return [sliceTextHtml(html, 0, cut), sliceTextHtml(html, cut, text.length)]
}

const FlowPrintLayout = ({ boxes, layout, dataSourceList, reportParams, onStatus }) => {
  const measureRef = useRef(null)
  const [calculated, setResult] = useState(null)
  const repeated = layout.flowRepeatDataSourceId !== undefined && layout.flowRepeatDataSourceId !== null && layout.flowRepeatDataSourceId !== ''
  const prepared = useMemo(() => {
    try {
      const groups = getReportFlowGroups(dataSourceList, layout.flowRepeatDataSourceId)
      const flowBoxes = Object.entries(boxes)
        .filter(([, item]) => item?.type === 'texteditor' && ['split', 'keep'].includes(item.flowMode))
        .sort((a, b) => Number(a[1].top) - Number(b[1].top))
      const sources = groups.flatMap((group) => flowBoxes.map(([boxId, item], index) => ({
        id: `${group.id}:${boxId}`, boxId, groupId: group.id, item,
        startNewPage: repeated && index === 0,
        html: DOMPurify.sanitize(renderTextEditorSourceCode(item.sourceCode, group.dataSourceList)),
      })))
      return { groups, sources }
    } catch (error) {
      return { groups: [], sources: [], error: error.message }
    }
  }, [boxes, dataSourceList, layout.flowRepeatDataSourceId, repeated])
  const { sources, groups } = prepared
  const result = calculated?.prepared === prepared ? calculated : null

  useEffect(() => {
    let canceled = false
    setResult(null)
    onStatus({ ready: false, pages: 0, error: '' })
    const calculate = async () => {
      try {
        if (prepared.error) throw new Error(prepared.error)
        if (Number(layout.pages || 1) !== 1) throw new Error('Flow รุ่นทดลองรองรับ template ต้นฉบับหน้าเดียวเท่านั้น')
        if (document.fonts?.ready) await document.fonts.ready
        await new Promise((resolve) => requestAnimationFrame(resolve))
        if (canceled) return
        const paper = measureRef.current.querySelector('.report-flow-paper-size')
        const bottom = paper.getBoundingClientRect().height - 44
        const sections = sources.map(({ id, item, html, groupId, startNewPage }) => {
          const host = Array.from(measureRef.current.querySelectorAll('[data-flow-measure]')).find((node) => node.dataset.flowMeasure === id)
          const parsed = document.createElement('div')
          parsed.innerHTML = html
          const content = parsed.querySelector('[data-flow-content]') || parsed
          const explicitBlocks = [...content.querySelectorAll('[data-flow-block]')]
          const blocks = (item.flowMode === 'split' && explicitBlocks.length ? explicitBlocks : [...content.children]).map((node) => node.outerHTML)
          if (!blocks.length) blocks.push(`<div>${escapeHtml(content.textContent)}</div>`)
          const measure = (parts, title) => {
            host.innerHTML = fragmentHtml(parts, title)
            return Math.ceil(host.getBoundingClientRect().height)
          }
          return { id, groupId, startNewPage, mode: item.flowMode, title: item.flowTitle || '', blocks, measure,
            split: (block, available, title) => splitMeasuredFlowBlock(block, available, title, measure) }
        })
        const firstTop = Number(sources[0].item.top)
        const nextTop = layout.flowContinuationTop === undefined ? (repeated ? firstTop : 150) : Number(layout.flowContinuationTop)
        const pages = paginateReportFlow({ sections, firstTop, nextTop, bottom })
        if (canceled) return
        setResult({ pages, prepared })
        onStatus({ ready: true, pages: pages.length, error: '' })
      } catch (error) {
        if (!canceled) {
          setResult({ error: error.message, prepared })
          onStatus({ ready: false, pages: 0, error: error.message })
        }
      }
    }
    calculate()
    return () => { canceled = true }
  }, [prepared, sources, layout, repeated, onStatus])

  const sectionStyle = (item) => ({ width: item.width || '700px', fontSize: item.fontSize || '12px', lineHeight: item.lineHeight || '20px' })
  const fontFamily = { angsana: 'font-angsana', THSarabunNew: 'font-sarabun-new', Prompt: 'font-prompt', sarabun: 'font-sarabun' }
  return <>
    <div ref={measureRef} className={`report-flow-measure ${fontFamily[layout.fontFamily] || 'font-sarabun'}`} aria-hidden="true">
      <div className="report-flow-paper-size" style={{ height: layout.height }} />
      {sources.map(({ id, item, html }) => <React.Fragment key={id}>
        {item.styleData && <style>{item.styleData}</style>}
        <div data-flow-measure={id} className="report-flow-content" style={sectionStyle(item)}
          dangerouslySetInnerHTML={{ __html: fragmentHtml([html], item.flowTitle) }} />
      </React.Fragment>)}
    </div>
    {!result && <div role="status" className="bg-white p-3">กำลังวัดเนื้อหาและจัดหน้า...</div>}
    {result?.error && <div role="alert" className="bg-white text-danger p-3">จัดหน้าไม่สำเร็จ: {result.error} — ยังไม่อนุญาตให้พิมพ์เพื่อป้องกันข้อมูลถูกตัด</div>}
    {result?.pages?.map((fragments, pageIndex) => {
      const groupId = fragments[0]?.groupId
      const group = groups.find((entry) => entry.id === groupId)
      const firstRecordPage = pageIndex === 0 || result.pages[pageIndex - 1][0]?.groupId !== groupId
      const fixedBoxes = Object.fromEntries(Object.entries(boxes).filter(([id, item]) =>
        !sources.some((source) => source.boxId === id) && (repeated ? (firstRecordPage || item.flowHeader) : (pageIndex === 0 ? !item.flowHeader : item.flowHeader))))
      return <div key={pageIndex} data-flow-page={pageIndex + 1} className={`print-page-sheet${pageIndex === result.pages.length - 1 ? ' last-print-page-sheet' : ''}`}>
        <PrintArea boxes={fixedBoxes} layout={layout} pageNo={pageIndex + 1} totalPage={result.pages.length}
          dataSourceList={group?.dataSourceList || dataSourceList} reportParams={reportParams} disabled showValue
          backgroundContent={<>
            {fragments.map((fragment) => {
              const { item } = sources.find((source) => source.id === fragment.id)
              return <div key={fragment.id} data-flow-fragment={fragment.id} className="report-flow-content"
                style={{ ...sectionStyle(item), position: 'absolute', left: item.left, top: fragment.top }}
                dangerouslySetInnerHTML={{ __html: fragmentHtml(fragment.blocks, fragment.title) }} />
            })}
            {result.pages.length > 1 && <div className="report-flow-page-number">หน้า {pageIndex + 1} / {result.pages.length}</div>}
          </>} />
      </div>
    })}
  </>
}

export default FlowPrintLayout
